import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import { normalizeImageKitUrl } from '@lic/media-urls'

type ImageAlign = 'left' | 'center' | 'right'

function ImageNodeView({ node, selected }: NodeViewProps) {
  const { src, alt, title, width, height, align, caption } = node.attrs
  const alignment = align === 'center'
    ? 'mx-auto'
    : align === 'right'
      ? 'ml-auto'
      : ''

  return (
    <NodeViewWrapper as="div" className={selected ? 'ProseMirror-selectednode' : ''}>
      <img
        src={normalizeImageKitUrl(src || '')}
        alt={alt || ''}
        title={title || undefined}
        width={width || undefined}
        height={height || undefined}
        data-align={align || undefined}
        data-caption={caption || undefined}
        className={alignment}
      />
    </NodeViewWrapper>
  )
}

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: element => element.getAttribute('data-align') || null,
        renderHTML: ({ align }) => {
          if (align === 'center') return { 'data-align': 'center', style: 'display:block;margin:0 auto' }
          if (align === 'right') return { 'data-align': 'right', style: 'display:block;margin-left:auto' }
          return {}
        },
      },
      caption: {
        default: null,
        parseHTML: element => element.getAttribute('data-caption') || null,
        renderHTML: ({ caption }) => caption ? { 'data-caption': caption } : {},
      },
    }
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView)
  },
})

import { useState, useEffect, useRef } from 'react'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Minus,
  Link as LinkIcon, Image as ImageIcon,
  ExternalLink, Pencil, Unlink, Trash2,
  AlignLeft, AlignCenter, AlignRight,
  Type, Highlighter,
} from 'lucide-react'
import AssetPickerModal from '@/components/AssetPickerModal'

const TEXT_COLORS = [
  { label: 'Reset', value: null },
  { label: 'White', value: '#ffffff' },
  { label: 'Zinc', value: '#a1a1aa' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Teal', value: '#14b8a6' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
] as const

const HIGHLIGHT_COLORS = [
  { label: 'Reset', value: null },
  { label: 'Yellow', value: '#fef08a' },
  { label: 'Green', value: '#bbf7d0' },
  { label: 'Blue', value: '#bfdbfe' },
  { label: 'Pink', value: '#fbcfe8' },
  { label: 'Orange', value: '#fed7aa' },
] as const

interface TipTapEditorProps {
  value: string
  onChange: (html: string) => void
}

function ToolbarButton({
  onClick, active, title, children,
}: {
  onClick: () => void
  active?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick() }}
      className={`p-1.5 rounded text-sm transition-colors ${
        active
          ? 'bg-zinc-600 text-white'
          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
      }`}
    >
      {children}
    </button>
  )
}

export default function TipTapEditor({ value, onChange }: TipTapEditorProps) {
  const [imagePickerOpen, setImagePickerOpen] = useState(false)
  const [linkMode, setLinkMode] = useState<'view' | 'edit'>('view')
  const [linkInputValue, setLinkInputValue] = useState('')
  const [tooltip, setTooltip] = useState<{ href: string; top: number; left: number } | null>(null)
  const [openPicker, setOpenPicker] = useState<'color' | 'highlight' | null>(null)

  const [imgTooltip, setImgTooltip] = useState<{
    top: number
    left: number
    width: number | null
    align: ImageAlign | null
    caption: string | null
  } | null>(null)

  const editorAreaRef = useRef<HTMLDivElement>(null)
  const hoverAnchorRef = useRef<HTMLAnchorElement | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hoverImgRef = useRef<HTMLImageElement | null>(null)
  const imgHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const imgTooltipRef = useRef<HTMLDivElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      CustomImage.configure({ inline: false }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'min-h-[400px] focus:outline-none text-sm text-zinc-200 leading-relaxed',
      },
    },
  })

  useEffect(() => {
    if (!editor) return

    const normalizeRenderedImages = () => {
      editor.view.dom.querySelectorAll<HTMLImageElement>('img[src]').forEach((image) => {
        const src = image.getAttribute('src')
        if (src) image.setAttribute('src', normalizeImageKitUrl(src))
      })
    }

    normalizeRenderedImages()
    const observer = new MutationObserver(normalizeRenderedImages)
    observer.observe(editor.view.dom, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] })
    return () => observer.disconnect()
  }, [editor])

  // Prevent links from navigating when clicked inside the editor
  useEffect(() => {
    const el = editorAreaRef.current
    if (!el) return
    const handler = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('a')) e.preventDefault()
    }
    el.addEventListener('click', handler, true)
    return () => el.removeEventListener('click', handler, true)
  }, [])

  // Hover tooltip: show when mouse enters a link, hide with delay on leave
  useEffect(() => {
    const el = editorAreaRef.current
    if (!el) return

    const clearHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }

    const scheduleHide = () => {
      clearHide()
      hideTimerRef.current = setTimeout(() => {
        setTooltip(null)
        setLinkMode('view')
        hoverAnchorRef.current = null
      }, 200)
    }

    const onMouseOver = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null
      if (!anchor) return
      clearHide()
      hoverAnchorRef.current = anchor
      const rect = anchor.getBoundingClientRect()
      setTooltip({
        href: anchor.getAttribute('href') ?? '',
        top: rect.bottom + 6,
        left: rect.left,
      })
    }

    const onMouseOut = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('a')) return
      const to = e.relatedTarget as HTMLElement | null
      if (to && tooltipRef.current?.contains(to)) return
      scheduleHide()
    }

    el.addEventListener('mouseover', onMouseOver)
    el.addEventListener('mouseout', onMouseOut)
    return () => {
      el.removeEventListener('mouseover', onMouseOver)
      el.removeEventListener('mouseout', onMouseOut)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [])

  // Hover toolbar for images
  useEffect(() => {
    const el = editorAreaRef.current
    if (!el) return

    const clearHide = () => {
      if (imgHideTimerRef.current) clearTimeout(imgHideTimerRef.current)
    }
    const scheduleHide = () => {
      clearHide()
      imgHideTimerRef.current = setTimeout(() => {
        setImgTooltip(null)
        hoverImgRef.current = null
      }, 200)
    }

    const onMouseOver = (e: MouseEvent) => {
      const img = e.target instanceof HTMLImageElement ? e.target : null
      if (!img) return
      clearHide()
      hoverImgRef.current = img
      const rect = img.getBoundingClientRect()
      const w = parseInt(img.getAttribute('width') ?? '') || null
      const a = (img.getAttribute('data-align') ?? null) as ImageAlign | null
      const c = img.getAttribute('data-caption') || null
      setImgTooltip({ top: rect.top + 8, left: rect.left + 8, width: w, align: a, caption: c })
    }

    const onMouseOut = (e: MouseEvent) => {
      if (!(e.target instanceof HTMLImageElement)) return
      const to = e.relatedTarget as HTMLElement | null
      if (to && imgTooltipRef.current?.contains(to)) return
      scheduleHide()
    }

    el.addEventListener('mouseover', onMouseOver)
    el.addEventListener('mouseout', onMouseOut)
    return () => {
      el.removeEventListener('mouseover', onMouseOver)
      el.removeEventListener('mouseout', onMouseOut)
      if (imgHideTimerRef.current) clearTimeout(imgHideTimerRef.current)
    }
  }, [])

  // Close color/highlight picker when clicking outside any [data-picker] element
  useEffect(() => {
    if (!openPicker) return
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-picker]')) setOpenPicker(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openPicker])

  if (!editor) return null

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('URL:', prev ?? '')
    if (url === null) return
    if (url === '') editor.chain().focus().unsetLink().run()
    else editor.chain().focus().setLink({ href: url }).run()
  }

  const insertImage = (url: string) => {
    editor.chain().focus().setImage({ src: url }).run()
  }

  // Select the full span of the link mark at the hovered anchor element
  const selectHoverLink = () => {
    const anchor = hoverAnchorRef.current
    if (!anchor) return
    try {
      const pos = editor.view.posAtDOM(anchor, 0)
      const { state } = editor
      const linkType = state.schema.marks.link
      let from = pos, to = pos + 1
      state.doc.nodesBetween(
        Math.max(0, pos - 500),
        Math.min(state.doc.content.size, pos + 500),
        (node, nodePos) => {
          if (node.isText && node.marks.some(m => m.type === linkType)) {
            from = Math.min(from, nodePos)
            to = Math.max(to, nodePos + node.nodeSize)
          }
        }
      )
      editor.chain().setTextSelection({ from, to }).run()
    } catch { /* ignore */ }
  }

  const handleUbah = () => {
    selectHoverLink()
    setLinkInputValue(hoverAnchorRef.current?.getAttribute('href') ?? tooltip?.href ?? '')
    setLinkMode('edit')
  }

  const handleBuka = () => {
    const href = hoverAnchorRef.current?.getAttribute('href') ?? tooltip?.href
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  const handleHapus = () => {
    selectHoverLink()
    editor.chain().unsetLink().run()
    setTooltip(null)
    hoverAnchorRef.current = null
  }

  const saveLinkEdit = () => {
    const trimmed = linkInputValue.trim()
    if (trimmed === '') editor.chain().unsetLink().run()
    else editor.chain().setLink({ href: trimmed }).run()
    setTooltip(null)
    setLinkMode('view')
    hoverAnchorRef.current = null
  }

  const cancelLinkEdit = () => setLinkMode('view')

  const handleImageSize = (width: number | null) => {
    const img = hoverImgRef.current
    if (!img) return
    try {
      const pos = editor.view.posAtDOM(img, 0)
      editor.chain().setNodeSelection(pos).updateAttributes('image', { width }).run()
      // TipTap replaces the DOM node on attribute update — refresh ref to new element
      const newNode = editor.view.nodeDOM(pos)
      if (newNode instanceof HTMLImageElement) hoverImgRef.current = newNode
      setImgTooltip(prev => prev ? { ...prev, width } : null)
    } catch { /* ignore */ }
  }

  const handleImageAlign = (align: ImageAlign | null) => {
    const img = hoverImgRef.current
    if (!img) return
    try {
      const pos = editor.view.posAtDOM(img, 0)
      const newAlign = align === 'left' ? null : align
      editor.chain().setNodeSelection(pos).updateAttributes('image', { align: newAlign }).run()
      const newNode = editor.view.nodeDOM(pos)
      if (newNode instanceof HTMLImageElement) hoverImgRef.current = newNode
      setImgTooltip(prev => prev ? { ...prev, align: newAlign } : null)
    } catch { /* ignore */ }
  }

  const handleImageCaption = (caption: string) => {
    const img = hoverImgRef.current
    if (!img) return
    try {
      const pos = editor.view.posAtDOM(img, 0)
      const newCaption = caption || null
      editor.chain().setNodeSelection(pos).updateAttributes('image', { caption: newCaption }).run()
      const newNode = editor.view.nodeDOM(pos)
      if (newNode instanceof HTMLImageElement) hoverImgRef.current = newNode
      setImgTooltip(prev => prev ? { ...prev, caption: newCaption } : null)
    } catch { /* ignore */ }
  }

  const handleImageDelete = () => {
    const img = hoverImgRef.current
    if (!img) return
    try {
      const pos = editor.view.posAtDOM(img, 0)
      editor.chain().setNodeSelection(pos).deleteSelection().run()
    } catch { /* ignore */ }
    setImgTooltip(null)
    hoverImgRef.current = null
  }

  const hideTooltip = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => {
      setTooltip(null)
      setLinkMode('view')
      hoverAnchorRef.current = null
    }, 200)
  }

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden focus-within:border-zinc-500 transition-colors">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-zinc-800/60 border-b border-zinc-700">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
          <Heading1 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">
          <ListOrdered size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
          <Quote size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} active={false} title="Horizontal rule">
          <Minus size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Link">
          <LinkIcon size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => setImagePickerOpen(true)} active={false} title="Insert image">
          <ImageIcon size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        {/* Text color picker */}
        <div data-picker="true" className="relative">
          <ToolbarButton
            onClick={() => setOpenPicker(prev => prev === 'color' ? null : 'color')}
            active={openPicker === 'color'}
            title="Text color"
          >
            <Type size={14} />
          </ToolbarButton>
          {openPicker === 'color' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-2">
              <div className="grid grid-cols-5 gap-1">
                {TEXT_COLORS.map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    title={label}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      if (value) editor.chain().focus().setColor(value).run()
                      else editor.chain().focus().unsetColor().run()
                      setOpenPicker(null)
                    }}
                    className={`w-6 h-6 rounded border transition-colors hover:border-zinc-300 flex items-center justify-center ${
                      value === null ? 'border-zinc-600 text-zinc-400 text-[10px] bg-zinc-900' : 'border-zinc-600'
                    }`}
                    style={value ? { backgroundColor: value } : {}}
                  >
                    {value === null ? '✕' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Text highlight picker */}
        <div data-picker="true" className="relative">
          <ToolbarButton
            onClick={() => setOpenPicker(prev => prev === 'highlight' ? null : 'highlight')}
            active={openPicker === 'highlight'}
            title="Text highlight"
          >
            <Highlighter size={14} />
          </ToolbarButton>
          {openPicker === 'highlight' && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-2">
              <div className="flex gap-1">
                {HIGHLIGHT_COLORS.map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    title={label}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      if (value) editor.chain().focus().setHighlight({ color: value }).run()
                      else editor.chain().focus().unsetHighlight().run()
                      setOpenPicker(null)
                    }}
                    className={`w-6 h-6 rounded border transition-colors hover:border-zinc-300 flex items-center justify-center ${
                      value === null ? 'border-zinc-600 text-zinc-400 text-[10px] bg-zinc-900' : 'border-zinc-600'
                    }`}
                    style={value ? { backgroundColor: value } : {}}
                  >
                    {value === null ? '✕' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Editor area */}
      <div ref={editorAreaRef} className="px-4 py-3 bg-zinc-900 tiptap-content">
        <EditorContent editor={editor} />
      </div>

      {/* Hover link tooltip */}
      {tooltip && (
        <div
          ref={tooltipRef}
          style={{ position: 'fixed', top: tooltip.top, left: tooltip.left, zIndex: 50 }}
          onMouseEnter={() => {
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
          }}
          onMouseLeave={hideTooltip}
        >
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl text-xs overflow-hidden">
            {linkMode === 'view' ? (
              <div className="flex items-center gap-0.5 px-2 py-1.5">
                <span className="max-w-[180px] truncate text-zinc-400 px-1">{tooltip.href}</span>
                <div className="w-px h-3.5 bg-zinc-600 mx-1" />
                <button
                  type="button"
                  onClick={handleUbah}
                  className="flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                >
                  <Pencil size={11} /> Ubah
                </button>
                <button
                  type="button"
                  onClick={handleBuka}
                  className="flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                >
                  <ExternalLink size={11} /> Buka
                </button>
                <div className="w-px h-3.5 bg-zinc-600 mx-1" />
                <button
                  type="button"
                  onClick={handleHapus}
                  className="p-1 rounded hover:bg-zinc-700 text-red-400 hover:text-red-300 transition-colors"
                >
                  <Unlink size={11} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <input
                  type="url"
                  value={linkInputValue}
                  autoFocus
                  onChange={(e) => setLinkInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveLinkEdit() }
                    if (e.key === 'Escape') { e.preventDefault(); cancelLinkEdit() }
                  }}
                  placeholder="https://..."
                  className="w-48 bg-zinc-900 text-zinc-200 border border-zinc-600 rounded px-2 py-1 text-xs focus:outline-none focus:border-yellow-500 placeholder:text-zinc-600"
                />
                <button
                  type="button"
                  onClick={saveLinkEdit}
                  className="px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-medium transition-colors"
                >
                  Simpan
                </button>
                <button
                  type="button"
                  onClick={cancelLinkEdit}
                  className="px-2 py-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  Batal
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hover image toolbar */}
      {imgTooltip && (
        <div
          ref={imgTooltipRef}
          style={{ position: 'fixed', top: imgTooltip.top, left: imgTooltip.left, zIndex: 50 }}
          onMouseEnter={() => { if (imgHideTimerRef.current) clearTimeout(imgHideTimerRef.current) }}
          onMouseLeave={() => {
            if (imgHideTimerRef.current) clearTimeout(imgHideTimerRef.current)
            imgHideTimerRef.current = setTimeout(() => {
              setImgTooltip(null)
              hoverImgRef.current = null
            }, 200)
          }}
        >
          <div className="bg-zinc-800/90 border border-zinc-700 rounded-lg shadow-xl text-xs overflow-hidden">
            {/* Size / align / delete row */}
            <div className="flex items-center gap-0.5 px-2 py-1.5">
              {([
                { label: 'S', width: 240 },
                { label: 'M', width: 480 },
                { label: 'L', width: 720 },
                { label: 'Full', width: null },
              ] as const).map(({ label, width }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleImageSize(width)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    imgTooltip.width === width
                      ? 'bg-yellow-500 text-black'
                      : 'text-zinc-300 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  {label}
                </button>
              ))}
              <div className="w-px h-3.5 bg-zinc-600 mx-1" />
              {([
                { align: 'left' as const, icon: <AlignLeft size={11} /> },
                { align: 'center' as const, icon: <AlignCenter size={11} /> },
                { align: 'right' as const, icon: <AlignRight size={11} /> },
              ]).map(({ align, icon }) => (
                <button
                  key={align}
                  type="button"
                  onClick={() => handleImageAlign(align)}
                  className={`p-1.5 rounded transition-colors ${
                    (imgTooltip.align ?? 'left') === align
                      ? 'bg-yellow-500 text-black'
                      : 'text-zinc-300 hover:text-white hover:bg-zinc-700'
                  }`}
                >
                  {icon}
                </button>
              ))}
              <div className="w-px h-3.5 bg-zinc-600 mx-1" />
              <button
                type="button"
                onClick={handleImageDelete}
                className="p-1 rounded hover:bg-zinc-700 text-red-400 hover:text-red-300 transition-colors"
              >
                <Trash2 size={11} />
              </button>
            </div>
            {/* Caption input row */}
            <div className="px-2 pb-1.5 border-t border-zinc-700/50 pt-1.5">
              <input
                type="text"
                value={imgTooltip.caption ?? ''}
                onChange={(e) => setImgTooltip(prev => prev ? { ...prev, caption: e.target.value } : null)}
                onBlur={(e) => handleImageCaption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleImageCaption(imgTooltip.caption ?? '') }
                }}
                placeholder="Add caption…"
                className="w-full bg-zinc-900 text-zinc-200 border border-zinc-700 rounded px-2 py-1 text-xs focus:outline-none focus:border-yellow-500 placeholder:text-zinc-600"
              />
            </div>
          </div>
        </div>
      )}

      {imagePickerOpen && (
        <AssetPickerModal
          onSelect={(url) => { insertImage(url); setImagePickerOpen(false) }}
          onClose={() => setImagePickerOpen(false)}
        />
      )}
    </div>
  )
}
